/**
 * トレーニング経過(2026-10-08 発注者指示。D-197)。メニュー「スタッフ」。admin だけ。
 *  - 一覧: 在籍中の全スタッフ × カテゴリ(給湯器/不用品)の 累計の実稼働・取得・1時間あたり と、15h/30h/60h/90h の区間の成績
 *  - 面談の時期: 新人が 15h/30h/60h に届いて、その段階の面談の記録が無い人(記録すると消える)
 *  - 行を押すと右に詳細(?track=)。面談・研修の記録の追加・編集と、面談の前後15hの成績
 *  - 段階の成績は「到達の成績を保存」で控えを残す(画面を開いただけでは書かない)
 * 数字は training.ts(画面で計算しない)。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, matchKeyword } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import HelpTip from '@core/components/HelpTip';
import DataTable, { type DataTableColumn } from '@core/components/ui/DataTable';
import { SplitView } from '@core/components/ui/DetailPanel';
import FilterBar from '@core/components/ui/FilterBar';
import PageHeader from '@core/components/ui/PageHeader';
import AlertPanel, { type AlertItem } from '@core/components/ui/AlertPanel';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import { fmtRate, fmtSec, partialNote, RateDiff, TargetRate, TargetValue } from './trainingFormat';
import { fetchOperators } from '@core/masters/operatorRepo';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchSummariesRange } from '@core/dashboard/dashboardRepo';
import { todayStr, toDateLabel, toShortDateLabel } from '@core/lib/date';
import { addDays } from '@core/dashboard/calc';
import DateField from '@core/components/DateField';
import { BUSINESS_CODES, BUSINESS_LABELS, type BusinessType, type Company, type DateStr, type Operator } from '@core/lib/types';
import { formatCount, formatDecimal, NO_DATA } from '@core/utils/formatters';
import { buildTracks, displayMilestones, EFFECT_HOURS, effectRows, hoursBefore, latestEffectByTrack, monthlyAverages, planAlerts, planTargets, planWindowStats, RECORD_KIND_LABELS, rateOf, rateTarget, SHEET_UNTIL, trainingAlerts, unsavedMilestones, type Milestone, type MilestoneResult, type SavedMilestone, type Track, type TrainingRecord, type TrainingTrackDoc } from './training';
import { fetchSavedMilestones, fetchTrainingRecords, fetchTrainingTracks, saveMilestones } from './trainingRepo';
import TrainingDetailPanel from './TrainingDetailPanel';
import TrainingRecordModal, { type RecordDraft } from './TrainingRecordModal';
import TrainingImportModal from './TrainingImportModal';
import TrainingStartDateModal from './TrainingStartDateModal';
import TrainingInterviewImportModal from './TrainingInterviewImportModal';
import TrainingTrainerRenameModal from './TrainingTrainerRenameModal';
import TrainingEffectsView from './TrainingEffectsView';

type Scope = 'new' | 'all';
/** この段階から先の列は畳める(2026-10-15 発注者指示「150h以降はエクセルのように折りたたみ」) */
const FOLD_FROM = 150;
/** 15h〜90h の列の並べ替えの基準: 区間の1時間あたり / 前の段階との差(2026-10-09 発注者指示) */
type MilestoneSort = 'rate' | 'diff';
/** 段階の欄に出すもの: 経過(前の段階からの区間)と累計(稼働開始からその段階まで)の両方 / どちらか(2026-10-14 発注者指示) */
type MilestoneView = 'both' | 'seg' | 'cum';
const BUSINESS_OPTIONS = (Object.keys(BUSINESS_LABELS) as BusinessType[]).map((key) => ({ key, label: `${BUSINESS_CODES[key]} ${BUSINESS_LABELS[key]}` }));

/**
 * 一覧の段階のセル(2026-10-14 発注者指示で2行に): 上=経過(前の段階からの区間の件/h と前の段階との差)、下=累計(稼働開始からその段階までの件/h)。
 * 目標のある段階(30h・60h。給湯器)は、経過・累計のどちらも目標以上なら緑、未満なら赤
 * 「経過」「累計」の文字は最初の段階(15h)の列の左側にだけ出し、それ以降の列は数字だけ(2026-10-14 発注者指示)
 */
function MilestoneCell({ m, target, view, tags }: { m: MilestoneResult | undefined; target: number | null; view: MilestoneView; tags: boolean }) {
  if (!m) return <span className="ui-hint">{NO_DATA}</span>;
  const showTags = tags && view === 'both';
  return (
    <span className={`training-ms${showTags ? ' is-tagged' : ''}`} title={`${toDateLabel(m.reachedDate)} 到達 ・ 区間 ${m.segAppointments}件 / ${formatDecimal(m.segHours, { digits: 2 })}h ・ 累計 ${m.cumAppointments}件 / ${formatDecimal(m.cumHours, { digits: 2 })}h`}>
      {view !== 'cum' && (
        <span className="training-ms-line">
          {showTags && <span className="training-ms-tag">経過</span>}
          <span className="training-ms-val">
            <TargetRate value={m.segRate} target={target} />
            {m.diff !== null && (
              <span className="training-diff">
                (<RateDiff value={m.diff} />)
              </span>
            )}
          </span>
        </span>
      )}
      {view !== 'seg' && (
        <span className="training-ms-line">
          {showTags && <span className="training-ms-tag">累計</span>}
          <span className="training-ms-val">
            <TargetRate value={m.cumRate} target={target} />
            {m.cumDiff !== null && (
              <span className="training-diff">
                (<RateDiff value={m.cumDiff} />)
              </span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

const isPermissionError = (e: unknown) => e instanceof Error && /permission|権限/i.test(e.message);

export default function TrainingPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [trackDocs, setTrackDocs] = useState<TrainingTrackDoc[]>([]);
  const [records, setRecords] = useState<TrainingRecord[]>([]);
  const [saved, setSaved] = useState<SavedMilestone[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [scope, setScope] = useState<Scope>('new');
  const [milestoneSort, setMilestoneSort] = useState<MilestoneSort>('rate');
  const [businesses, setBusinesses] = useState<Set<BusinessType>>(() => new Set());
  const [keyword, setKeyword] = useState('');
  const [msView, setMsView] = useState<MilestoneView>('both');
  const [draft, setDraft] = useState<RecordDraft | null>(null);
  const [importing, setImporting] = useState(false);
  const [bulkStart, setBulkStart] = useState(false);
  const [importingInterviews, setImportingInterviews] = useState(false);
  const [renamingTrainer, setRenamingTrainer] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = todayStr();

  const selectedKey = params.get('track');
  /** 表示: スタッフの一覧 / 面談の効果(まとめて比べる。2026-10-09) */
  const view: 'list' | 'effects' = params.get('view') === 'effects' ? 'effects' : 'list';
  const setView = (v: 'list' | 'effects') => {
    const p = new URLSearchParams(params);
    if (v === 'effects') p.set('view', 'effects');
    else p.delete('view');
    setParams(p);
  };
  /** 面談の効果: この日以降に行った面談だけで比べる(毎週の会議の報告用。2026-10-09 発注者指示)。URL ?from=YYYY-MM-DD */
  const fromParam = params.get('from');
  const effectsFrom: DateStr | null = fromParam && /^\d{4}-\d{2}-\d{2}$/.test(fromParam) ? fromParam : null;
  const setEffectsFrom = (d: DateStr | null) => {
    const p = new URLSearchParams(params);
    if (d) p.set('from', d);
    else p.delete('from');
    setParams(p);
  };
  const select = (key: string | null) => {
    const p = new URLSearchParams(params);
    if (key) p.set('track', key);
    else p.delete('track');
    setParams(p);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [ops, cos, docs, recs, sv, sums] = await Promise.all([
        fetchOperators(),
        fetchCompanies(),
        fetchTrainingTracks(),
        fetchTrainingRecords(),
        fetchSavedMilestones(),
        fetchSummariesRange(SHEET_UNTIL, today),
      ]);
      setOperators(ops);
      setCompanies(cos);
      setTrackDocs(docs);
      setRecords(recs);
      setSaved(sv);
      setTracks(buildTracks({ operators: ops, summaries: sums, trackDocs: docs, today }));
    } catch (e) {
      setLoadError(
        isPermissionError(e)
          ? 'トレーニング経過のデータを読む権限がありません(データベースの権限ルールの反映が必要です)'
          : e instanceof Error
            ? `読み込みに失敗しました: ${e.message}`
            : '読み込みに失敗しました',
      );
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const recordsByTrack = useMemo(() => {
    const m = new Map<string, TrainingRecord[]>();
    for (const r of records) {
      const k = `${r.op_id}_${r.business}`;
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return m;
  }, [records]);

  const filtered = useMemo(
    () =>
      tracks
        .filter((t) => scope === 'all' || t.isNew)
        // カテゴリのプルダウンは面談の効果の表示だけ(スタッフ一覧は見出しのメニューで絞る)
        .filter((t) => (view === 'list' ? true : matchesFilter(businesses, t.business)))
        .filter((t) => matchKeyword(keyword, t.name, t.opCode)),
    [tracks, scope, businesses, keyword, view],
  );
  /** 段階の列: 15h〜90h と、表に出ている人のだれかが届いた 120h・150h… */
  const msList = useMemo(() => displayMilestones(filtered), [filtered]);
  // 150h 以降の段階の列は Excel のグループのように畳める(既定は畳む。2026-10-15 発注者指示)
  const [lateOpen, setLateOpen] = useState(false);
  const lateList = msList.filter((m) => m >= FOLD_FROM);
  const shownMs = lateOpen ? msList : msList.filter((m) => m < FOLD_FROM);

  const alerts = useMemo(() => trainingAlerts(tracks, records), [tracks, records]);
  const plans = useMemo(() => planAlerts(tracks, trackDocs, records, todayStr()), [tracks, trackDocs, records]);
  const effects = useMemo(() => effectRows(tracks, records), [tracks, records]);
  /** 月別の全員の平均(カテゴリごと。詳細の「月別の成績」に使う) */
  const monthAvg = useMemo(() => ({ kyutoki: monthlyAverages(tracks, 'kyutoki'), fuyohin: monthlyAverages(tracks, 'fuyohin') }), [tracks]);
  const latestEffect = useMemo(() => latestEffectByTrack(effects), [effects]);
  // 面談の効果の表示も、一覧と同じ絞り込み(新人/全員・カテゴリ・名前)を使う。担当者の名前でも探せる
  const filteredEffects = useMemo(
    () =>
      effects
        .filter((r) => r.track && (scope === 'all' || r.track.isNew))
        .filter((r) => matchesFilter(businesses, r.record.business))
        .filter((r) => matchKeyword(keyword, r.track?.name ?? '', r.track?.opCode ?? '', r.record.trainer))
        .filter((r) => !effectsFrom || r.record.date >= effectsFrom),
    [effects, scope, businesses, keyword, effectsFrom],
  );
  const unsaved = useMemo(() => unsavedMilestones(tracks, saved), [tracks, saved]);
  const selected = selectedKey ? (tracks.find((t) => t.key === selectedKey) ?? null) : null;

  const openDraft = (t: Track, milestone: Milestone | null, record?: TrainingRecord) => {
    const bs = tracks.filter((x) => x.opId === t.opId).map((x) => x.business);
    setDraft({ opId: t.opId, name: t.name, businesses: bs.length ? bs : [t.business], business: t.business, milestone, record });
  };

  async function saveUnsaved() {
    if (!user || unsaved.length === 0) return;
    setSaving(true);
    try {
      const n = await saveMilestones(unsaved, user.id);
      toast(`到達の成績を ${formatCount(n)}件 保存しました`);
      setSaved(await fetchSavedMilestones());
    } catch (e) {
      toast(e instanceof Error ? `保存できませんでした: ${e.message}` : '保存できませんでした');
    } finally {
      setSaving(false);
    }
  }


  const columns: DataTableColumn<Track>[] = [
    { key: 'name', header: '氏名', width: 140, sticky: true, nowrap: true, filter: 'values', sortValue: (t) => t.name, render: (t) => <span className="op-name">{t.name}</span> },
    { key: 'business', header: 'カテゴリ', width: 84, nowrap: true, filter: 'values', filterLabel: (v) => BUSINESS_LABELS[v as BusinessType] ?? String(v), sortValue: (t) => t.business, render: (t) => BUSINESS_LABELS[t.business] },
    {
      key: 'start',
      header: '稼働開始',
      width: 96, // 日付は「26/09/28」の形(2026-10-14 発注者指示「幅を取りすぎ」)
      nowrap: true,
      filter: 'values',
      filterLabel: (v) => toShortDateLabel(String(v)),
      sortValue: (t) => t.startDate,
      // 稼働開始日そのものを出す(2026-10-09 発注者指示)。不明は「—」(9/1 から数えている)
      render: (t) => (t.startDate ? toShortDateLabel(t.startDate) : <span className="ui-hint" title={`稼働開始日が不明のため ${toDateLabel(t.countFrom)} から数えています`}>{NO_DATA}</span>),
    },
    {
      key: 'lastWork',
      header: '最終稼働日',
      width: 104,
      nowrap: true,
      filter: 'values',
      filterLabel: (v) => toShortDateLabel(String(v)),
      sortValue: (t) => t.lastWorkDate,
      render: (t) => (t.lastWorkDate ? toShortDateLabel(t.lastWorkDate) : <span className="ui-hint">{NO_DATA}</span>),
    },
    { key: 'hours', header: '累計稼働', width: 84, numeric: true, filter: 'range', sortValue: (t) => t.totalHours, render: (t) => formatDecimal(t.totalHours, { digits: 1, unit: 'h' }) },
    { key: 'appointments', header: '取得', width: 64, numeric: true, filter: 'range', sortValue: (t) => t.totalAppointments, render: (t) => formatCount(t.totalAppointments) },
    { key: 'rate', header: '件/h', width: 64, numeric: true, filter: 'range', sortValue: (t) => rateOf(t.totalAppointments, t.totalHours), render: (t) => fmtRate(rateOf(t.totalAppointments, t.totalHours)) },
    // 通話の数字(数え始めから。採用データの日は入らない。2026-10-20 発注者指示)。色は給湯器の目標(面談予定日の目標と同じ)
    {
      key: 'talkAvg',
      header: '通話時間Av',
      width: 92,
      numeric: true,
      filter: 'range',
      sortValue: (t) => t.calls.talkAvgSec,
      render: (t) => <TargetValue value={t.calls.talkAvgSec} target={planTargets(t.business)?.talkAvgSec ?? null} higher={false} text={fmtSec(t.calls.talkAvgSec)} unit="秒" />,
    },
    {
      key: 'afterAvg',
      header: '後処理時間Av',
      width: 100,
      numeric: true,
      filter: 'range',
      sortValue: (t) => t.calls.afterAvgSec,
      render: (t) => <TargetValue value={t.calls.afterAvgSec} target={planTargets(t.business)?.afterAvgSec ?? null} higher={false} text={fmtSec(t.calls.afterAvgSec)} unit="秒" />,
    },
    {
      key: 'callsPerHour',
      header: '通話回数/h',
      width: 88,
      numeric: true,
      filter: 'range',
      sortValue: (t) => t.calls.callsPerHour,
      render: (t) => <TargetValue value={t.calls.callsPerHour} target={planTargets(t.business)?.callsPerHour ?? null} higher text={formatDecimal(t.calls.callsPerHour, { digits: 1 })} unit="回" />,
    },
    ...shownMs.map((m): DataTableColumn<Track> => {
      const target = rateTarget('kyutoki', m);
      return {
        key: `m${m}`,
        // 目標のある段階は見出しに目標を出す(給湯器)
        header:
          target !== null ? (
            // 目標は段階の下の行に出す(2026-10-14 発注者指示「目標は改行して」)
            <span className="training-ms-head">
              {m}h
              <span className="training-ms-goal">目標{fmtRate(target)}</span>
            </span>
          ) : milestoneSort === 'diff' ? (
            `${m}h(差)`
          ) : (
            `${m}h`
          ),
        label: `${m}h`,
        // 「経過」「累計」の文字を出すのは最初の段階の列だけ。それ以降は数字だけなので細く
        width: msView === 'both' && m === msList[0] ? 128 : msView === 'both' ? 108 : 100,
        numeric: true,
        nowrap: true,
        // 絞り込み: 表示している件/h の以上・以下。目標のある 30h・60h はそれに「目標達成/目標未達」(累計で判定)のチェックも足す(2026-10-14 発注者指示)
        filter: 'range' as const,
        filterValue: (t: Track) => {
          const r = t.milestones.find((x) => x.milestone === m);
          return (msView === 'cum' ? r?.cumRate : r?.segRate) ?? null;
        },
        ...(target !== null
          ? {
              filterGroup: (t: Track) => {
                const r = t.milestones.find((x) => x.milestone === m);
                const tg = rateTarget(t.business, m);
                if (!r || r.cumRate === null || tg === null) return null;
                return Math.round(r.cumRate * 100) / 100 >= tg ? '目標達成' : '目標未達';
              },
            }
          : {}),
        sortValue: (t) => {
          const r = t.milestones.find((x) => x.milestone === m);
          return (msView === 'cum' ? (milestoneSort === 'diff' ? r?.cumDiff : r?.cumRate) : milestoneSort === 'diff' ? r?.diff : r?.segRate) ?? null;
        },
        render: (t) => <MilestoneCell m={t.milestones.find((x) => x.milestone === m)} target={rateTarget(t.business, m)} view={msView} tags={m === msList[0]} />,
      };
    }),
    // 150h 以降を畳んでいるときは、その人が届いた一番先の段階を1列で出す(開く/畳むは上の「並べ替え」の隣のボタン)
    ...(lateList.length && !lateOpen
      ? [
          {
            key: 'msFold',
            header: `${FOLD_FROM}h〜`,
            width: 88,
            nowrap: true,
            render: (t: Track) => {
              const top = t.milestones.filter((x) => x.milestone >= FOLD_FROM).at(-1);
              return top ? <span className="ui-hint">{top.milestone}h 到達</span> : <span className="ui-hint">{NO_DATA}</span>;
            },
          } satisfies DataTableColumn<Track>,
        ]
      : []),
    {
      key: 'next',
      header: '次の段階',
      width: 128, // 「60hまで 24.9h」が切れない幅(2026-10-09 実測 115px+余白)
      nowrap: true,
      filter: 'values',
      filterValue: (t) => (t.next ? `${t.next.milestone}hまで` : null),
      sortValue: (t) => (t.next ? t.next.remaining : null),
      render: (t) => (t.next ? `${t.next.milestone}hまで ${formatDecimal(t.next.remaining, { digits: 1 })}h` : NO_DATA),
    },
    // 面談の効果は 最後の面談日・面談前15h・面談後15h・効果 に分けて出す(2026-10-12 発注者指示「面談の効果はわからない」)
    {
      key: 'lastDate',
      header: '最後の面談日',
      width: 136,
      nowrap: true,
      filter: 'values',
      filterLabel: (v) => toShortDateLabel(String(v)),
      sortValue: (t) => latestEffect.get(t.key)?.record.date ?? null,
      render: (t) => {
        const r = latestEffect.get(t.key);
        if (!r) return <span className="ui-hint">{NO_DATA}</span>;
        return (
          <>
            {toShortDateLabel(r.record.date)}
            {/* 面談した時点が稼働何時間目か(その日より前の累計。2026-10-14 発注者指示) */}
            {r.track && <span className="ui-hint training-partial">({formatDecimal(hoursBefore(r.track.days, r.record.date), { digits: 1 })}h時点)</span>}
            {r.record.kind !== 'interview' && <span className="ui-hint training-partial">{RECORD_KIND_LABELS[r.record.kind]}</span>}
          </>
        );
      },
    },
    {
      key: 'before',
      header: `面談前${EFFECT_HOURS}h 件/h`,
      width: 104,
      numeric: true,
      filter: 'range',
      sortValue: (t) => latestEffect.get(t.key)?.effect.before?.rate ?? null,
      render: (t) => {
        const r = latestEffect.get(t.key);
        return r?.effect.before ? fmtRate(r.effect.before.rate) : <span className="ui-hint">{NO_DATA}</span>;
      },
    },
    {
      key: 'after',
      header: `面談後${EFFECT_HOURS}h 件/h`,
      width: 140,
      numeric: true,
      nowrap: true,
      filter: 'range',
      sortValue: (t) => latestEffect.get(t.key)?.effect.after?.rate ?? null,
      render: (t) => {
        const r = latestEffect.get(t.key);
        if (!r?.effect.after) return <span className="ui-hint">{r?.effect.before ? '後の稼働なし' : NO_DATA}</span>;
        const note = partialNote(r.effect);
        return (
          <>
            {fmtRate(r.effect.after.rate)}
            {note && <span className="ui-hint training-partial">{note}</span>}
          </>
        );
      },
    },
    {
      key: 'effect',
      header: '効果',
      width: 80,
      numeric: true,
      // 面談後 − 面談前(1時間あたりの取得の差)
      filter: 'range',
      sortValue: (t) => latestEffect.get(t.key)?.effect.diff ?? null,
      render: (t) => {
        const d = latestEffect.get(t.key)?.effect.diff ?? null;
        return d === null ? <span className="ui-hint">{NO_DATA}</span> : <RateDiff value={d} />;
      },
    },
  ];

  const mobileCard = (t: Track) => (
    <>
      <div className="op-card-head">
        <span className="op-name">{t.name}</span>
        <span className="ui-hint">{BUSINESS_LABELS[t.business]}</span>
      </div>
      <div className="op-card-row">
        <span>累計稼働／取得／件/h</span>
        <span className="is-numeric">
          {formatDecimal(t.totalHours, { digits: 1, unit: 'h' })}／{formatCount(t.totalAppointments)}／{fmtRate(rateOf(t.totalAppointments, t.totalHours))}
        </span>
      </div>
      <div className="op-card-row">
        <span>次の段階</span>
        <span>{t.next ? `${t.next.milestone}hまで ${formatDecimal(t.next.remaining, { digits: 1 })}h` : '90h 到達'}</span>
      </div>
    </>
  );

  // 上の欄: 面談予定者(予定日の早い順)→ 面談が要対応の人(2026-10-15 発注者指示)
  const planItems: AlertItem[] = plans.map(({ track: t, date, overdue }) => {
    const w = planWindowStats(t.days, date);
    return {
      id: `plan_${t.key}`,
      severity: overdue ? 'warning' : 'info',
      label: overdue ? '予定超過' : '予定',
      target: `${t.name}(${BUSINESS_LABELS[t.business]})`,
      message: `面談予定日 ${toDateLabel(date)}${overdue ? '(過ぎています。面談の記録がありません)' : ''}`,
      diff: w.days ? `予定日の前30日 稼働 ${formatDecimal(w.hours, { digits: 1, unit: 'h' })} ・ ${fmtRate(w.rate)}件/h` : '予定日の前30日の稼働はありません',
      action: { label: '面談を記録', onClick: () => openDraft(t, null) },
    };
  });
  const alertItems: AlertItem[] = alerts.map(({ track: t, milestone: m }) => ({
    id: `${t.key}_${m.milestone}`,
    severity: m.milestone === 15 ? 'warning' : 'danger',
    target: `${t.name}(${BUSINESS_LABELS[t.business]})`,
    message: `${m.milestone}h に届きました(${toDateLabel(m.reachedDate)})。${m.milestone}h の面談の記録がありません`,
    diff: (
      <>
        区間 {m.segAppointments}件 / {formatDecimal(m.segHours, { digits: 2 })}h = {fmtRate(m.segRate)}件/h
        {m.diff !== null && (
          <>
            {' '}
            ・ 前の段階から <RateDiff value={m.diff} />
          </>
        )}
      </>
    ),
    action: { label: '面談を記録', onClick: () => openDraft(t, m.milestone) },
  }));

  return (
    <div className="screen full is-fill training-screen">
      <SplitView
        panel={
          selected && (
            <TrainingDetailPanel
              track={selected}
              trackDoc={trackDocs.find((d) => d.id === selected.key)}
              records={recordsByTrack.get(selected.key) ?? []}
              saved={saved}
              monthAverages={selected.business === 'fuyohin' ? monthAvg.fuyohin : monthAvg.kyutoki}
              companies={companies}
              userId={user?.id ?? ''}
              onAdd={() => openDraft(selected, null)}
              onEdit={(r) => openDraft(selected, r.milestone, r)}
              onChanged={() => void reload()}
              onClose={() => select(null)}
            />
          )
        }
      >
        <PageHeader
          title="トレーニング経過"
          // 表を広く見せるため、表示の切り替え・件数・操作を1行にまとめる(2026-10-12 発注者指示)
          meta={
            <span className="training-head">
              <SegmentedControl
                label="表示"
                size="sm"
                options={[
                  { key: 'list', label: 'スタッフ一覧' },
                  { key: 'effects', label: '面談の効果(まとめて比べる)' },
                ]}
                value={view}
                onChange={setView}
              />
              <span>{loading ? '読み込み中' : `${formatCount(filtered.length)}件(在籍 ${formatCount(new Set(tracks.map((t) => t.opId)).size)}名)`}</span>
            </span>
          }
          actions={
            <>
              {/* 最新のデータを読み直す(2026-10-14 発注者指示) */}
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => void reload()} disabled={loading}>
                {loading ? '読み込み中…' : 'データを更新'}
              </button>
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setImportingInterviews(true)} disabled={loading || !!loadError}>
                面談記録を取り込む
              </button>
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setBulkStart(true)} disabled={loading || !!loadError}>
                稼働開始日をまとめて変更
              </button>
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setImporting(true)} disabled={loading || !!loadError}>
                採用データを取り込む
              </button>
              <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" onClick={() => void saveUnsaved()} disabled={saving || unsaved.length === 0} title="15h/30h/60h/90h に届いたときの成績を控えとして保存します">
                {saving ? '保存中…' : unsaved.length > 0 ? `到達の成績を保存(${formatCount(unsaved.length)}件)` : '到達の成績は保存済み'}
              </button>
            </>
          }
        />
        {loadError && <AlertPanel title="読み込めませんでした" items={[{ id: 'error', severity: 'danger', target: 'トレーニング経過', message: loadError }]} />}
        {!loadError && !loading && view === 'list' && (
          <AlertPanel
            title="面談の予定・時期"
            meta="面談予定日を入れた人と、新人が 15h・30h・60h に届いてその段階の面談の記録が無い人"
            items={[...planItems, ...alertItems]}
            emptyMessage="面談が必要な人はいません"
            compact
          />
        )}
        <FilterBar
          // 表の下にあった説明は、表を広く見せるため吹き出しに移した(2026-10-12)
          actions={
            view === 'list' && (
              <HelpTip help={{ title: '表の見方', what: '段階(15h・30h・60h・90h、その後 30h ごと)の欄 = 上段「経過」は前の段階からの区間の1時間あたり取得、下段「累計」は稼働開始からその段階までの1時間あたり取得(かっこ内はどちらも前の段階との差)。目標は 30h で 1.5件/h・60h で 2.0件/h 以上(給湯器。以上は緑、未満は赤。不用品は目標なし)。最終稼働日 = 最後に稼働した日。最後の面談日の(○h時点) = 面談した日より前の累計稼働。累計稼働 = 数え始めからの実稼働の合計。新人 = 5/1 以降に稼働を始めた人。それ以外は 9/1 から数えます。面談前15h・面談後15h = 最後の面談・研修の日(指導は入れない)の前後15時間の1時間あたり取得(面談日は含めない。後が15hに届いていなければその時点の数字)。効果 = 面談後 − 面談前。通話時間Av・後処理時間Av・通話回数/h = 数え始めからの 通話時間 ÷ 通話回数・後処理時間 ÷ 後処理回数・通話回数 ÷ 実稼働(日次集計の日だけ。採用データの日は通話の記録が無いので入りません。給湯器は目標 通話22秒以下・後処理6秒以下・80回以上で、満たせば緑・満たさなければ赤)。', better: '件/h は高いほど良い' }}>
                <span className="ui-hint training-help">表の見方</span>
              </HelpTip>
            )
          }
          // スタッフ一覧の絞り込みは見出しのメニュー(Excel のフィルタのように)で行う。上の絞り込みは面談の効果の表示だけ(2026-10-14 発注者指示「これは不要」)
          moreActiveCount={view === 'effects' && businesses.size > 0 ? 1 : 0}
          more={view === 'effects' ? <MultiSelect label="カテゴリ" options={BUSINESS_OPTIONS} value={businesses} onChange={setBusinesses} /> : undefined}
        >
          <SearchBox className="ui-search" value={keyword} onChange={setKeyword} placeholder={view === 'effects' ? '名前・管理番号・担当者で検索' : '名前・管理番号で検索'} />
          <SegmentedControl
            label="対象"
            options={[
              { key: 'new', label: '新人' },
              { key: 'all', label: '全員' },
            ]}
            value={scope}
            onChange={setScope}
          />
          {view === 'effects' && (
            <>
              <SegmentedControl
                label="面談日"
                showLabel
                options={[
                  { key: 'all', label: 'すべて' },
                  { key: 'from', label: 'この日以降' },
                ]}
                value={effectsFrom ? 'from' : 'all'}
                // 「この日以降」を選んだら、まず1週間前の日を入れる(毎週の会議の報告用)
                onChange={(k) => setEffectsFrom(k === 'from' ? addDays(today, -7) : null)}
              />
              {effectsFrom && <DateField variant="ui" value={effectsFrom} onChange={setEffectsFrom} />}
            </>
          )}
          {view === 'list' && (
            <>
              <SegmentedControl
                label="段階の表示"
                showLabel
                options={[
                  { key: 'both', label: '経過と累計' },
                  { key: 'seg', label: '経過' },
                  { key: 'cum', label: '累計' },
                ]}
                value={msView}
                onChange={setMsView}
              />
              <SegmentedControl
                label="並べ替え"
                showLabel
                options={[
                  { key: 'rate', label: '件/h' },
                  { key: 'diff', label: '前との差' },
                ]}
                value={milestoneSort}
                onChange={setMilestoneSort}
              />
              {/* 150h 以降の列を開く/畳む(並べ替えの隣。2026-10-15 発注者指示) */}
              {lateList.length > 0 && (
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" aria-expanded={lateOpen} onClick={() => setLateOpen((v) => !v)}>
                  {lateOpen ? `－ ${FOLD_FROM}h〜を畳む` : `＋ ${FOLD_FROM}h〜を開く`}
                </button>
              )}
            </>
          )}
        </FilterBar>
        {view === 'effects' ? (
          <TrainingEffectsView rows={filteredEffects} from={effectsFrom} selectedKey={selected?.key ?? null} onSelect={select} onRenameTrainer={() => setRenamingTrainer(true)} />
        ) : (
          <DataTable
            caption="トレーニング経過"
            columns={columns}
            headerMenu
            rows={filtered}
            rowKey={(t) => t.key}
            loading={loading}
            initialSort={{ key: 'start', dir: 'desc' }}
            defaultPageSize={100}
            emptyMessage={scope === 'new' ? '新人がいません(「全員」で既存スタッフも出ます)' : '在籍中のスタッフがいません'}
            onRowClick={(t) => select(t.key)}
            selectedKey={selected?.key ?? null}
            mobile="cards"
            renderMobileCard={mobileCard}
          />
        )}
      </SplitView>

      {draft && user && (
        <TrainingRecordModal
          draft={draft}
          userId={user.id}
          userName={user.name}
          onClose={() => setDraft(null)}
          onSaved={() => {
            setDraft(null);
            void reload();
          }}
        />
      )}
      {renamingTrainer && user && (
        <TrainingTrainerRenameModal
          records={records}
          userId={user.id}
          onClose={() => setRenamingTrainer(false)}
          onDone={() => {
            setRenamingTrainer(false);
            void reload();
          }}
        />
      )}
      {importingInterviews && user && (
        <TrainingInterviewImportModal
          operators={operators}
          records={records}
          userId={user.id}
          userName={user.name}
          onClose={() => setImportingInterviews(false)}
          onDone={() => {
            setImportingInterviews(false);
            void reload();
          }}
        />
      )}
      {bulkStart && user && (
        <TrainingStartDateModal
          operators={operators}
          existing={trackDocs}
          userId={user.id}
          onClose={() => setBulkStart(false)}
          onDone={() => {
            setBulkStart(false);
            void reload();
          }}
        />
      )}
      {importing && user && (
        <TrainingImportModal
          operators={operators}
          existing={trackDocs}
          userId={user.id}
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            void reload();
          }}
        />
      )}
    </div>
  );
}
